/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { func, shape, arrayOf, bool } from 'prop-types';
import { Form, Grid } from 'semantic-ui-react';
import { Formik, Field } from 'formik';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import {
  SingleDatePickerField,
  DropdownField,
  MaskedInputField,
  RadioField,
  InputField,
  TextAreaField,
} from 'Modules/Formik';
import moment from 'moment';
import useClinicDetail from 'Containers/Home/hooks/useClinicDetail';
import AntDatePickerField from 'Components/Form/AntdDatePickerField';

import ModalAnnexDetails from '../AnnexDetails';

import { validateDigi } from './validation';
import styles from './MCDigi.less';
import { findDigiType } from './helpers';

const numberMask = createNumberMask({
  prefix: '',
  allowDecimal: true,
  includeThousandsSeparator: true,
  decimalLimit: 10,
  integerLimit: 8,
});

const optionTypes = list =>
  list.map(({ id, name }) => ({
    key: id,
    value: id,
    text: name,
  }));

const MCDigiForm = forwardRef((props, ref) => {
  const { onSubmit, initialValues, types, isNeedValidationAge } = props;
  const [isOpenAnnex, setOpenAnnex] = useState(false);
  let mHandleSubmit = {};
  useImperativeHandle(
    ref,
    () => ({
      handleSubmit: mHandleSubmit,
    }),
    [],
  );

  const { clinicId } = initialValues;
  const { clinicDetail } = useClinicDetail(clinicId);

  const handleChangeType = (name, value, values, setFieldValue) => {
    findDigiType(types, value).fields.forEach(field => {
      if (values[field.key] === undefined) {
        setFieldValue(field.key, null);
      }
    });
  };

  const renderDateFieldByType = digiTypeId => {
    const digiType = findDigiType(types, digiTypeId);

    if (!digiType) return null;

    return digiType.fields.map(item => (
      <Grid.Column key={item.key}>
        <AntDatePickerField
          label={`${item.name} (*)`}
          name={item.key}
          showTime={item.dataType === 'datetime'}
          errorPosition="bottom"
        />
      </Grid.Column>
    ));
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      // validationSchema={DigiMCSchema}
      validate={values => validateDigi(values, types, isNeedValidationAge)}>
      {({ handleSubmit, values, setFieldValue }) => {
        mHandleSubmit = handleSubmit;
        const { digiType, fitday } = values;
        return (
          <Form className={styles.container}>
            <Field label="Name (*)" name="patientName" component={InputField} />
            <Field
              label="NRIC / Passport Number (*)"
              name="patientNric"
              onlyTyNumberAndLetter
              component={InputField}
            />
            <div style={{ marginBottom: 16 }}>
              <div className={styles.annexValue}>
                <div style={{ marginRight: 8 }}>
                  <strong>Send Advisory to this patient with ARI</strong>
                </div>
                <Field name="isAnnex" component={RadioField} toggle />
              </div>
              <a className={styles.annexDetails} onClick={() => setOpenAnnex(true)}>
                View details
              </a>
            </div>
            <Field
              label="Type of medical (*)"
              name="digiType"
              component={DropdownField}
              options={optionTypes(types)}
              onChange={(name, value) => handleChangeType(name, value, values, setFieldValue)}
            />
            <Grid style={{ marginBottom: 0 }}>
              <Grid.Row columns="equal" style={{ paddingTop: '0.3rem' }}>
                {renderDateFieldByType(digiType)}
              </Grid.Row>
            </Grid>

            <div style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
              <Field name="fitday" component={RadioField} toggle />
              <span style={{ marginLeft: '1em' }}>Fit for light duty</span>
            </div>
            {fitday && (
              <Grid style={{ marginBottom: 0 }}>
                <Grid.Row columns={2}>
                  <Grid.Column>
                    <Field
                      label="From (*)"
                      name="fitFrom"
                      component={SingleDatePickerField}
                      openDirection="up"
                      isOutsideRange={day => moment.duration(day.diff(moment())).asDays() + 2 < 0}
                      // isOutsideRange={day => false}
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <Field
                      label="To (*)"
                      name="fitTo"
                      component={SingleDatePickerField}
                      openDirection="up"
                      isOutsideRange={day => moment.duration(day.diff(moment())).asDays() + 2 < 0}
                      // isOutsideRange={day => false}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            )}
            <Grid style={{ marginBottom: 0, marginTop: 0 }}>
              <Grid.Row columns={2}>
                <Grid.Column>
                  <Field
                    label="No of day (*)"
                    name="days"
                    component={MaskedInputField}
                    excludeSeparator
                    mask={numberMask}
                    type="number"
                  />
                </Grid.Column>
                <Grid.Column>
                  <Field
                    label="Start date (*)"
                    name="startDate"
                    placeholder="Start date"
                    component={SingleDatePickerField}
                    openDirection="up"
                    isOutsideRange={day => moment.duration(day.diff(moment())).asDays() + 2 < 0}
                    // isOutsideRange={day => false}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>

            <div style={{ marginBottom: '14px' }}>Clinic: {clinicDetail?.name}</div>
            <Field label="Comment" name="comments" component={TextAreaField} />
            {isOpenAnnex && <ModalAnnexDetails onClose={() => setOpenAnnex(false)} />}
          </Form>
        );
      }}
    </Formik>
  );
});

MCDigiForm.propTypes = {
  initialValues: shape({}).isRequired,
  onSubmit: func.isRequired,
  types: arrayOf(shape({})).isRequired,
  isNeedValidationAge: bool.isRequired,
};

MCDigiForm.defaultProps = {};

export default MCDigiForm;
